import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	TelematicsMileageClient,
	MileageSummaryDto,
	MileageHistoryDto,
} from '@acorn-portal/shared/src/services/telematicsMileageApi';

interface TelematicsDriverMileageState {
	// Mileage summary
	summary: MileageSummaryDto | null;
	summaryLoading: boolean;
	summaryError: string | null;
	// Mileage history
	history: MileageHistoryDto | null;
	historyLoading: boolean;
	historyError: string | null;
}

const initialState: TelematicsDriverMileageState = {
	summary: null,
	summaryLoading: false,
	summaryError: null,
	history: null,
	historyLoading: false,
	historyError: null,
};

interface FetchMileageSummaryParams {
	policyReference: string;
}

interface FetchMileageHistoryParams {
	policyReference: string;
	period?: 'weekly' | 'monthly';
}

export const fetchMileageSummary = createAsyncThunk<
	MileageSummaryDto,
	FetchMileageSummaryParams,
	{ rejectValue: unknown }
>('telematicsDriverMileage/fetchMileageSummary', async (params: FetchMileageSummaryParams, { rejectWithValue }) => {
	try {
		const client = new TelematicsMileageClient(undefined, API);
		const response = await client.summary(params.policyReference);
		return response;
	} catch (e) {
		if (axios.isAxiosError(e) && e.response) {
			if (e.response.status < 200 || e.response.status > 300) {
				return rejectWithValue(e);
			}
		}
		if (typeThrownException(e) && e.status) {
			if (e.status < 200 || e.status > 300) {
				return rejectWithValue(e);
			}
		}
		throw e;
	}
});

export const fetchMileageHistory = createAsyncThunk<
	MileageHistoryDto,
	FetchMileageHistoryParams,
	{ rejectValue: unknown }
>('telematicsDriverMileage/fetchMileageHistory', async (params: FetchMileageHistoryParams, { rejectWithValue }) => {
	try {
		const client = new TelematicsMileageClient(undefined, API);
		const response = await client.history(params.policyReference, params.period);
		return response;
	} catch (e) {
		if (axios.isAxiosError(e) && e.response) {
			if (e.response.status < 200 || e.response.status > 300) {
				return rejectWithValue(e);
			}
		}
		if (typeThrownException(e) && e.status) {
			if (e.status < 200 || e.status > 300) {
				return rejectWithValue(e);
			}
		}
		throw e;
	}
});

export const telematicsDriverMileageSlice = createSlice({
	name: 'telematicsDriverMileage',
	initialState,
	reducers: {
		setMileageSummary: (state: TelematicsDriverMileageState, action: PayloadAction<MileageSummaryDto>) => {
			state.summary = action.payload;
		},
		setMileageHistory: (state: TelematicsDriverMileageState, action: PayloadAction<MileageHistoryDto>) => {
			state.history = action.payload;
		},
		clearMileageSummary: (state: TelematicsDriverMileageState) => {
			state.summary = null;
			state.summaryLoading = false;
			state.summaryError = null;
		},
		clearMileageHistory: (state: TelematicsDriverMileageState) => {
			state.history = null;
			state.historyLoading = false;
			state.historyError = null;
		},
		clearAll: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsDriverMileageState>) => {
		// Mileage Summary
		builder.addCase(fetchMileageSummary.pending, (state: TelematicsDriverMileageState) => {
			state.summaryLoading = true;
			state.summaryError = null;
		});
		builder.addCase(fetchMileageSummary.fulfilled, (state: TelematicsDriverMileageState, { payload }) => {
			state.summaryLoading = false;
			state.summary = payload;
		});
		builder.addCase(fetchMileageSummary.rejected, (state: TelematicsDriverMileageState, action) => {
			state.summaryLoading = false;
			state.summaryError = action.error.message ?? 'Failed to fetch mileage summary';
		});

		// Mileage History
		builder.addCase(fetchMileageHistory.pending, (state: TelematicsDriverMileageState) => {
			state.historyLoading = true;
			state.historyError = null;
		});
		builder.addCase(fetchMileageHistory.fulfilled, (state: TelematicsDriverMileageState, { payload }) => {
			state.historyLoading = false;
			state.history = payload;
		});
		builder.addCase(fetchMileageHistory.rejected, (state: TelematicsDriverMileageState, action) => {
			state.historyLoading = false;
			state.historyError = action.error.message ?? 'Failed to fetch mileage history';
		});
	},
});

export const { setMileageSummary, setMileageHistory, clearMileageSummary, clearMileageHistory, clearAll } =
	telematicsDriverMileageSlice.actions;
export default telematicsDriverMileageSlice.reducer;
